import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import ViolationDetailsTable from '../components/Table/ViolationDetailsTable'
import { ViolationModel } from '@/app/models'
import { useViolations } from '@/modules/drivers/hooks/useViolations'
import ViolationsSingleActionBar from '../components/ViolationsSingleActionBar'
import { getViolationName } from '@/app/services/violation/violationNameResolver'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { DatePickerRangeProps } from '@/modules/drivers/types'
import { useDate } from '@/modules/drivers/stores/dateStore'
import useContractService from '@/modules/drivers/services/ContractService'
import moment from 'moment'
import { formatPln } from '../formatPln'
const ViolationsSingle = ({ driverId }: { driverId: any }) => {
  const { companyId } = useParams<{ companyId: string }>()
  const { getViolations } = useViolations()
  const { range } = useDate()

  const { getContracts } = useContractService()
  const [violations, setViolations] = React.useState<ViolationModel[]>([])
  const [search, setSearch] = React.useState('')
  const [isLoadingDetails, setIsLoadingDetails] = React.useState(false)
  const [refreshKey, setRefreshKey] = React.useState(0)
  const [contracts, setContracts] = React.useState([])
  const [daysWithContractInPeriod, setDaysWithContractInPeriod] =
    React.useState(0)

  // Przy wejściu leci też żądanie ze starym zakresem ze store'u - jego odpowiedź nie może nadpisać nowszej.
  const latestRequestRef = React.useRef(0)
  const fetchDetails = async () => {
    const requestId = ++latestRequestRef.current
    setIsLoadingDetails(true)
    try {
      const data = await getViolations({ driverId: driverId })
      if (requestId === latestRequestRef.current) setViolations(data || [])
    } catch (err) {
      console.error('Error fetching violation details:', err)
    } finally {
      if (requestId === latestRequestRef.current) setIsLoadingDetails(false)
    }
  }

  React.useEffect(() => {
    if (!companyId) return
    const fetchContracts = async () => {
      try {
        const data = await getContracts({ companyId, driverId })
        setContracts(data || [])
      } catch (err) {
        console.error('Error fetching contracts:', err)
      }
    }
    fetchContracts()
  }, [driverId, companyId])

  const getDaysWithContractInPeriod = (
    contracts: Array<{ startDate: string; endDate: string | null }>,
    periodStart: Date,
    periodEnd: Date,
  ): number => {
    const msPerDay = 86400000
    const totalDays =
      Math.round((periodEnd.getTime() - periodStart.getTime()) / msPerDay) + 1
    if (contracts.length === 0) return totalDays

    let count = 0
    for (let i = 0; i < totalDays; i++) {
      const day = new Date(
        periodStart.getFullYear(),
        periodStart.getMonth(),
        periodStart.getDate() + i,
      )
      const hasActive = contracts.some((c) => {
        const start = moment(c.startDate).startOf('day')
        const end = c.endDate ? moment(c.endDate).endOf('day') : null
        return (
          moment(day).isSameOrAfter(start) &&
          (end === null || moment(day).isSameOrBefore(end))
        )
      })
      if (hasActive) count++
    }
    return count
  }
  useEffect(() => {
    if (contracts.length > 0 && range?.startDate && range?.endDate) {
      const days = getDaysWithContractInPeriod(
        contracts,
        new Date(
          range.startDate.year,
          range.startDate.month - 1,
          range.startDate.day,
        ),
        new Date(
          range.endDate.year,
          range.endDate.month - 1,
          range.endDate.day,
        ),
      )
      setDaysWithContractInPeriod(days)
    }
  }, [contracts, range])

  React.useEffect(() => {
    if (driverId) {
      fetchDetails()
    }
  }, [driverId, range?.startDate?.fullDate, range?.endDate?.fullDate, refreshKey])
  const { t } = useLocalize()

  const filteredViolations = React.useMemo(() => {
    if (!search || search.trim() === '') {
      return violations
    }
    const searchLower = search.toLowerCase().trim()
    return violations.filter((violation) => {
      const description =
        t(getViolationName(violation?.type)).toLowerCase() || ''
      return description.includes(searchLower)
    })
  }, [violations, search, t])
  const selectedDate = {
    startDate: range?.startDate ?? null,
    endDate: range?.endDate ?? null,
  }
  return (
    <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden">
      <ViolationsSingleActionBar
        search={search}
        setSearch={setSearch}
        searchResults={filteredViolations.length}
        driverId={driverId}
        onRefreshed={() => setRefreshKey((key) => key + 1)}
      />
      <ViolationDetailsTable
        violations={filteredViolations}
        isLoadingDetails={isLoadingDetails}
        driverId={driverId}
      />
      <ViolationFooterBar
        violations={violations}
        selectedDate={selectedDate}
        daysWithContractInPeriod={daysWithContractInPeriod}
      />
    </div>
  )
}

export default ViolationsSingle

type ViolationFooterBarProps = {
  violations: ViolationModel[]
  selectedDate: DatePickerRangeProps
  daysWithContractInPeriod: number
}

const getCompanyWeight = (weight: string): string => {
  const parts = (weight || '').split('\\')
  const company = (parts[1] ?? '').trim()
  return company === '-' || company === '' ? '' : company
}

const ViolationFooterBar: React.FC<ViolationFooterBarProps> = ({
  violations,
  selectedDate,
  daysWithContractInPeriod,
}) => {
  const companyWeights = violations.map((v) => getCompanyWeight(v.weight))
  const count = (type: string) =>
    companyWeights.filter((w) => w === type).length

  const lnn = count('NN')
  const lbpn = count('BPN')
  const lpn = count('PN')

  const daysInPeriod = (() => {
    const { startDate, endDate } = selectedDate
    if (!startDate || !endDate) return 0
    const s = new Date(startDate.year, startDate.month - 1, startDate.day)
    const e = new Date(endDate.year, endDate.month - 1, endDate.day)
    return Math.round((e.getTime() - s.getTime()) / 86400000) + 1
  })()

  const cwpn =
    daysInPeriod > 0 && daysWithContractInPeriod > 0
      ? (lbpn + lpn / 3.0) / (daysWithContractInPeriod / daysInPeriod)
      : lbpn + lpn / 3.0

  const driverCharge = violations.reduce((s, v) => s + (v.driverCharge || 0), 0)
  const companyCharge = violations.reduce(
    (s, v) => s + (v.companyCharge || 0),
    0,
  )

  const fmt = (d: DatePickerRangeProps['startDate']) =>
    d
      ? `${String(d.day).padStart(2, '0')}.${String(d.month).padStart(2, '0')}.${d.year}`
      : '—'

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-5 tw-border-t-2 tw-border-base-300 tw-bg-base-100 tw-px-4 tw-py-2 tw-text-[13px] tw-font-medium tw-text-[#343434]">
      <span>
        <span className="tw-font-normal tw-text-[#666]">CWPN: </span>
        <strong>{cwpn.toFixed(2).replace('.', ',')}</strong>
      </span>
      <span>
        <span className="tw-font-normal tw-text-[#666]">LNN: </span>
        <strong>{lnn}</strong>
      </span>
      <span className="tw-ml-auto tw-text-[#555]">
        {fmt(selectedDate?.startDate)} – {fmt(selectedDate?.endDate)}
      </span>
      <span>
        <span className="tw-font-normal tw-text-[#666]">Kara kierowcy: </span>
        <strong>{formatPln(driverCharge)}</strong>
      </span>
      <span>
        <span className="tw-font-normal tw-text-[#666]">Kara firmy: </span>
        <strong>{formatPln(companyCharge)}</strong>
      </span>
    </div>
  )
}
